import React from 'react';
import PropTypes from 'prop-types';
// Model
import mDevice from 'models/device';
import mSimcard from 'models/simcard';
import { TYPES as deviceModelTypes } from 'models/deviceModel';
// HOCs
import wrapListFilters from 'hocs/WrapListFilters';
// Custom
import { listFeatureSearchFieldShape, listFilterShape } from 'constants/PropTypesShapes';
import { yesNoAsInvertedList, toBooleanOrNull, toIntegerOrNull } from 'utils/Tools';
import { dateGenerator } from 'utils/DateUtils';
// Components
import FiltersTemplate from 'components/templates/FiltersTemplate';
import BaseListFilter from 'components/filters/BaseListFilter';
import TextFieldFilter from 'components/filters/TextFieldFilter';
import DateFilter from 'components/filters/DateFilter';
import ItemsSelectFieldFilter from 'components/filters/ItemsSelectFieldFilter';
import DistributionAutoCompleteFilter from 'distributions/assets/main/DistributionAutoCompleteFilter';
import InstallationAutoCompleteFilter from 'installations/assets/main/InstallationAutoCompleteFilter';
import SimcardAutoCompleteFilter from 'simcards/assets/main/SimcardAutoCompleteFilter';
import OperatorSelectFieldFilter from 'simcards/assets/operators/OperatorSelectFieldFilter';
import CompanySelectFieldFilter from 'companies/assets/main/CompanySelectFieldFilter';
import DeviceBatchSelectFieldFilter from 'production/assets/deviceBatch/DeviceBatchSelectFieldFilter';
import DeviceModelSelectFieldFilter from 'deviceModels/assets/main/DeviceModelSelectFieldFilter';

const DevicesFilters = ({
  disabled, advanced, nbFiltersApplied, searchFields, field, filters,
  isFieldDisplayable, onFieldChange, onFilterFieldChange, onReinit, onSwitch,
}) => {
  const {
    serial, timeout, deviceModelId, distribution, beginLastComm, endLastComm, ipAddress,
    available, installation, simcardStatus, simcard, operatorId, deviceBatchId, companyId,
  } = filters;
  // Dates
  const beginLastCommDate = dateGenerator.date(beginLastComm.value);
  const endLastCommDate = dateGenerator.date(endLastComm.value);

  return (
    <FiltersTemplate
      disabled={disabled}
      advanced={advanced}
      nbFiltersApplied={nbFiltersApplied}
      onReinit={onReinit}
      onSwitch={onSwitch}
    >
      <div className="row m-0 pb-3">
        {!advanced && (
          <div className="col-12 col-md-4 col-xl-3">
            <BaseListFilter
              field={field}
              searchFields={searchFields}
              onChange={onFieldChange}
            />
          </div>
        )}
        {isFieldDisplayable('serial') && (
          <div className="col-12 col-md-4 col-xl-3">
            <TextFieldFilter
              label="field.serial"
              value={serial.value}
              onChange={(value) => onFilterFieldChange('serial', value)}
            />
          </div>
        )}
        {isFieldDisplayable('timeout') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.status"
              value={toBooleanOrNull(timeout.value)}
              items={mDevice.statusList}
              onChange={(value) => onFilterFieldChange('timeout', value)}
            />
          </div>
        )}
        {isFieldDisplayable('deviceModelId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DeviceModelSelectFieldFilter
              deviceModelId={toIntegerOrNull(deviceModelId.value)}
              allowedTypes={[deviceModelTypes.opac]}
              onChange={(value) => onFilterFieldChange('deviceModelId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('distribution') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DistributionAutoCompleteFilter
              distribution={distribution.value}
              onSelect={(value) => onFilterFieldChange('distribution', value)}
            />
          </div>
        )}
        {isFieldDisplayable('beginLastComm') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DateFilter
              labelId="field.afterLastComm"
              type="begin"
              value={beginLastCommDate}
              onChange={(value) => onFilterFieldChange('beginLastComm', value)}
            />
          </div>
        )}
        {isFieldDisplayable('endLastComm') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DateFilter
              labelId="field.beforeLastComm"
              type="end"
              value={endLastCommDate}
              onChange={(value) => onFilterFieldChange('endLastComm', value)}
            />
          </div>
        )}
        {isFieldDisplayable('available') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.installationAttached"
              value={toBooleanOrNull(available.value)}
              items={yesNoAsInvertedList}
              onChange={(value) => onFilterFieldChange('available', value)}
            />
          </div>
        )}
        {isFieldDisplayable('installation') && (
          <div className="col-12 col-md-4 col-xl-3">
            <InstallationAutoCompleteFilter
              installation={installation.value}
              onSelect={(value) => onFilterFieldChange('installation', value)}
            />
          </div>
        )}
        {isFieldDisplayable('simcardStatus') && (
          <div className="col-12 col-md-4 col-xl-3">
            <ItemsSelectFieldFilter
              labelId="field.simcardStatus"
              value={simcardStatus.value}
              items={mSimcard.statusList}
              onChange={(value) => onFilterFieldChange('simcardStatus', value)}
            />
          </div>
        )}
        {isFieldDisplayable('simcard') && (
          <div className="col-12 col-md-4 col-xl-3">
            <SimcardAutoCompleteFilter
              simcard={simcard.value}
              onSelect={(value) => onFilterFieldChange('simcard', value)}
            />
          </div>
        )}
        {isFieldDisplayable('operatorId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <OperatorSelectFieldFilter
              operatorId={toIntegerOrNull(operatorId.value)}
              onChange={(value) => onFilterFieldChange('operatorId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('deviceBatchId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <DeviceBatchSelectFieldFilter
              deviceBatchId={toIntegerOrNull(deviceBatchId.value)}
              onChange={(value) => onFilterFieldChange('deviceBatchId', value)}
            />
          </div>
        )}
        {isFieldDisplayable('ipAddress') && (
          <div className="col-12 col-md-4 col-xl-3">
            <TextFieldFilter
              label="field.ipAddress"
              value={ipAddress.value}
              onChange={(value) => onFilterFieldChange('ipAddress', value)}
            />
          </div>
        )}
        {isFieldDisplayable('companyId') && (
          <div className="col-12 col-md-4 col-xl-3">
            <CompanySelectFieldFilter
              companyId={toIntegerOrNull(companyId.value)}
              onChange={(value) => onFilterFieldChange('companyId', value)}
            />
          </div>
        )}
      </div>
    </FiltersTemplate>
  );
};

DevicesFilters.propTypes = {
  // From HOC
  disabled: PropTypes.bool.isRequired,
  advanced: PropTypes.bool.isRequired,
  nbFiltersApplied: PropTypes.number.isRequired,
  searchFields: PropTypes.arrayOf(listFeatureSearchFieldShape).isRequired,
  field: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    serial: listFilterShape,
    timeout: listFilterShape,
    deviceModelId: listFilterShape,
    distribution: listFilterShape,
    beginLastComm: listFilterShape,
    endLastComm: listFilterShape,
    ipAddress: listFilterShape,
    available: listFilterShape,
    installation: listFilterShape,
    simcardStatus: listFilterShape,
    simcard: listFilterShape,
    operatorId: listFilterShape,
    deviceBatchId: listFilterShape,
    companyId: listFilterShape,
  }).isRequired,
  isFieldDisplayable: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onFilterFieldChange: PropTypes.func.isRequired,
  onReinit: PropTypes.func.isRequired,
  onSwitch: PropTypes.func.isRequired,
};

module.exports = wrapListFilters(mDevice.listFeatures().searchFields)(DevicesFilters);



// WEBPACK FOOTER //
// ./routes/app/routes/devices/assets/main/DeviceFilters.js